import { useMemo, useState } from 'react'
import { compactColumnKey, searchColumns, toggleColumnPick } from './SampleColumnWindow.js'

type PickerColumn = { key: string; label: string }

type Props = {
  columns: PickerColumn[]
  shown: PickerColumn[]
  limit: number
  onChange: (picks: string[] | null) => void
}

/** "Samples…": search the read columns by name and pick which of them render, at most [limit]. */
export function SampleColumnPicker({ columns, shown, limit, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const shownKeys = useMemo(() => new Set(shown.map((c) => compactColumnKey(c.key))), [shown])
  const matches = useMemo(() => searchColumns(columns, query), [columns, query])
  const full = shownKeys.size >= limit

  return (
    <span className="relative flex items-center gap-2">
      <span data-testid="otu-sample-window-count" className="text-gray-500">
        showing {shown.length.toLocaleString()} of {columns.length.toLocaleString()}
      </span>
      <button data-testid="otu-sample-picker" className="btn btn-xs btn-outline" onClick={() => setOpen((o) => !o)}>
        Samples…
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-lg bg-white p-2 shadow-lg">
          <input
            type="search"
            autoFocus
            data-testid="otu-sample-picker-search"
            className="input input-bordered input-sm mb-2 w-full"
            placeholder="Search samples"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="mb-2 flex items-center gap-2">
            <button className="btn btn-xs" onClick={() => onChange(null)}>
              First {limit}
            </button>
            <button className="btn btn-xs" onClick={() => onChange([])}>
              None
            </button>
            <span className="ml-auto text-xs text-gray-500">
              {shownKeys.size} / {limit} picked
            </span>
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {matches.map((column) => {
              const checked = shownKeys.has(compactColumnKey(column.key))
              return (
                <li key={column.key}>
                  <label className="flex cursor-pointer items-center gap-2 p-1 hover:bg-gray-100">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={checked}
                      disabled={!checked && full}
                      onChange={() => onChange(toggleColumnPick(shown, column.key))}
                    />
                    <span className="truncate">{column.label}</span>
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </span>
  )
}
